import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { DARK_DIALOG, DARK_INPUT, MfaField, errMsg } from "./shared";

const REQUEST_STATUSES = ["pending_review", "contacted", "scheduled_offline", "declined", "closed", "cancelled"];
const PAID_STATUSES = ["created", "finding_workers", "partially_assigned", "fully_assigned", "completed", "cancelled", "on_hold"];

const Field = ({ label, children }) => <div><label className="text-xs font-semibold text-slate-400">{label}</label><div className="mt-1">{children}</div></div>;

function Shell({ title, desc, onClose, children }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${DARK_DIALOG} max-w-md`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle>{desc && <DialogDescription className="text-slate-400">{desc}</DialogDescription>}</DialogHeader>
        <div className="space-y-3">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

export function EditBookingDialog({ b, onClose, onDone }) {
  const [v, setV] = useState({ status: b.status, admin_note: b.admin_note || "", scheduled_date: b.scheduled_date, start_time: b.start_time, location: b.location, customer_name: b.customer_name, customer_phone: b.customer_phone, customer_email: b.customer_email || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((x) => ({ ...x, [k]: e.target ? e.target.value : e }));
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/admin/manage/bookings/${b.id}`, v); toast.success("Booking updated"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title={`Edit ${b.code}`} onClose={onClose}>
      <Field label="Status">
        <Select value={v.status} onValueChange={set("status")}>
          <SelectTrigger className={DARK_INPUT} data-testid="bm-edit-status"><SelectValue/></SelectTrigger>
          <SelectContent>{[...new Set([b.status, ...(b.mode === "request_only" ? REQUEST_STATUSES : PAID_STATUSES)])].map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date"><Input type="date" value={v.scheduled_date} onChange={set("scheduled_date")} className={DARK_INPUT} data-testid="bm-edit-date"/></Field>
        <Field label="Start time"><Input type="time" value={v.start_time} onChange={set("start_time")} className={DARK_INPUT} data-testid="bm-edit-time"/></Field>
      </div>
      <Field label="Location"><Input value={v.location} onChange={set("location")} className={DARK_INPUT} data-testid="bm-edit-location"/></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Customer name"><Input value={v.customer_name} onChange={set("customer_name")} className={DARK_INPUT} data-testid="bm-edit-name"/></Field>
        <Field label="Phone"><Input value={v.customer_phone} onChange={set("customer_phone")} className={DARK_INPUT} data-testid="bm-edit-phone"/></Field>
      </div>
      <Field label="Admin note"><Textarea rows={3} value={v.admin_note} onChange={set("admin_note")} className={DARK_INPUT} data-testid="bm-edit-note"/></Field>
      <p className="text-[11px] text-slate-500">Cancelling a paid booking does not refund automatically — issue refunds from Payment Management.</p>
      <div className="flex justify-end"><Button className="bg-orange-500 hover:bg-orange-600" disabled={busy} onClick={save} data-testid="bm-edit-save">Save changes</Button></div>
    </Shell>
  );
}

export function SoftDeleteDialog({ b, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const go = async () => {
    try { await api.post(`/admin/manage/bookings/${b.id}/soft-delete`, { reason }); toast.success("Booking moved to Deleted"); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Shell title={`Delete ${b.code}?`} desc="The booking is hidden from all lists, the calendar and the public tracker. It can be restored anytime." onClose={onClose}>
      <Field label="Reason (required)"><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Duplicate request" className={DARK_INPUT} data-testid="bm-soft-reason"/></Field>
      <div className="flex justify-end"><Button className="bg-amber-600 hover:bg-amber-700" disabled={!reason.trim()} onClick={go} data-testid="bm-soft-confirm">Delete booking</Button></div>
    </Shell>
  );
}

export function HardDeleteDialog({ b, onClose, onDone }) {
  const [v, setV] = useState({ confirm_code: "", current_password: "", mfa_code: "" });
  const [busy, setBusy] = useState(false);
  const ok = v.confirm_code.trim().toUpperCase() === b.code && v.current_password && v.mfa_code.length === 6;
  const go = async () => {
    setBusy(true);
    try { await api.post(`/admin/manage/bookings/${b.id}/hard-delete`, v); toast.success("Booking permanently deleted"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Permanently delete booking" onClose={onClose}>
      <div className="flex gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-200" data-testid="bm-hard-warning">
        <AlertTriangle size={16} className="shrink-0"/>This cannot be undone. Bookings with successful payments are blocked to preserve financial records.
      </div>
      <Field label={`Type ${b.code} to confirm`}><Input value={v.confirm_code} onChange={(e) => setV({ ...v, confirm_code: e.target.value })} className={`${DARK_INPUT} font-mono`} data-testid="bm-hard-code"/></Field>
      <Field label="Current password"><Input type="password" value={v.current_password} onChange={(e) => setV({ ...v, current_password: e.target.value })} className={DARK_INPUT} data-testid="bm-hard-password"/></Field>
      <MfaField value={v.mfa_code} onChange={(c) => setV({ ...v, mfa_code: c })} testid="bm-hard-mfa"/>
      <div className="flex justify-end"><Button className="bg-red-600 hover:bg-red-700" disabled={!ok || busy} onClick={go} data-testid="bm-hard-confirm">Delete permanently</Button></div>
    </Shell>
  );
}
